// src/features/october/hooks/usePayment.js
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderService } from '../../../services/orders/orderService';
import { PayFastService } from '../../../services/payments/payfastService';
import { EmailService } from '../../../services/emails/emailService';
import { useCart } from './useCart';
import { useAuth } from '../../../context/AuthContext';

export const usePayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const { cartItems, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Create order and initiate payment
  const initiatePayment = useCallback(async (customerData, shippingData) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Validate cart
      if (!cartItems || cartItems.length === 0) {
        throw new Error('Cart is empty');
      }

      // Calculate totals
      const totals = OrderService.calculateOrderTotals(cartItems, 0); // Free shipping for now

      // Prepare order data
      const orderData = {
        userId: user?.uid || null,
        customer: {
          email: customerData.email,
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          phone: customerData.phone
        },
        shippingAddress: {
          firstName: shippingData.firstName,
          lastName: shippingData.lastName,
          address: shippingData.address,
          city: shippingData.city,
          province: shippingData.province,
          postalCode: shippingData.postalCode
        },
        items: cartItems,
        totals: totals,
        payment: {
          method: 'payfast'
        },
        notes: customerData.notes || ''
      };

      // Validate order data
      const validation = OrderService.validateOrderData(orderData);
      if (!validation.isValid) {
        throw new Error(`Order validation failed: ${validation.errors.join(', ')}`);
      }

      // Create order in database
      const order = await OrderService.createOrder(orderData);
      setCurrentOrder(order);

      // Validate payment amount
      const amountValidation = PayFastService.validateAmount(order.total);
      if (!amountValidation.valid) {
        throw new Error(amountValidation.error);
      }

      // Setup return URLs
      const baseUrl = window.location.origin;
      const returnUrls = {
        success: `${baseUrl}/payment/success?order=${order.id}`,
        cancel: `${baseUrl}/payment/cancelled?order=${order.id}`,
        notify: 'https://us-central1-pontshoyarona.cloudfunctions.net/api/payfast/notify'
      };

      // Redirect to PayFast
      PayFastService.redirectToPayFast(order, returnUrls);

      return order;
    } catch (err) {
      console.error('Payment initiation error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [cartItems, clearCart, user]);

  // Handle payment success
  const handlePaymentSuccess = useCallback(async (orderId, paymentData) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Update order payment status
      const updatedOrder = await OrderService.updatePaymentStatus(orderId, {
        status: 'completed',
        transactionId: paymentData.transactionId,
        paymentReference: paymentData.paymentReference,
        paymentDate: new Date()
      });

      // Trigger customer confirmation email (non-blocking)
      EmailService.sendOrderConfirmation(updatedOrder).catch((emailError) => {
        console.error('Failed to send order confirmation email:', emailError);
      });

      // Clear cart on successful payment
      clearCart();

      setCurrentOrder(updatedOrder);
      return updatedOrder;
    } catch (err) {
      console.error('Payment success handling error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [clearCart]);

  // Handle payment failure
  const handlePaymentFailure = useCallback(async (orderId, paymentData) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Update order payment status
      const updatedOrder = await OrderService.updatePaymentStatus(orderId, {
        status: 'failed',
        transactionId: paymentData.transactionId,
        paymentReference: paymentData.paymentReference,
        paymentDate: new Date(),
      });

      // Notify customer about payment failure (non-blocking)
      EmailService.sendPaymentFailure(updatedOrder).catch((emailError) => {
        console.error('Failed to send payment failure email:', emailError);
      });

      setCurrentOrder(updatedOrder);
      return updatedOrder;
    } catch (err) {
      console.error('Payment failure handling error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Handle payment cancellation
  const handlePaymentCancellation = useCallback(async (orderId) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Update order status to cancelled
      const updatedOrder = await OrderService.updateOrderStatus(orderId, 'cancelled', 'Payment cancelled by user');
      
      setCurrentOrder(updatedOrder);
      return updatedOrder;
    } catch (err) {
      console.error('Payment cancellation handling error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Get order by ID
  const getOrder = useCallback(async (orderId) => {
    try {
      setIsProcessing(true);
      setError(null);

      const order = await OrderService.getOrderById(orderId);
      setCurrentOrder(order);
      return order;
    } catch (err) {
      console.error('Get order error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Get customer orders
  const getCustomerOrders = useCallback(async (email) => {
    try {
      setIsProcessing(true);
      setError(null);

      const orders = await OrderService.getOrdersByCustomer(email);
      return orders;
    } catch (err) {
      console.error('Get customer orders error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Process PayFast return
  const processPayFastReturn = useCallback(async (returnParams) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Parse return parameters
      const returnData = PayFastService.processReturn(returnParams);
      
      if (!returnData.paymentId) {
        throw new Error('Invalid payment return data');
      }

      // Get the order
      const order = await OrderService.getOrderById(returnData.paymentId);
      
      if (!order) {
        throw new Error('Order not found');
      }

      // Process based on payment status
      let updatedOrder;
      if (returnData.paymentStatus === 'COMPLETE') {
        updatedOrder = await handlePaymentSuccess(order.id, {
          transactionId: returnData.transactionId,
          paymentReference: returnData.transactionId
        });
      } else {
        updatedOrder = await handlePaymentFailure(order.id, {
          transactionId: returnData.transactionId,
          paymentReference: returnData.transactionId
        });
      }

      return {
        order: updatedOrder,
        success: returnData.paymentStatus === 'COMPLETE'
      };
    } catch (err) {
      console.error('PayFast return processing error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [handlePaymentSuccess, handlePaymentFailure]);

  // Check PayFast availability
  const isPayFastAvailable = useCallback(() => {
    return PayFastService.isAvailable();
  }, []);

  // Get payment config
  const getPaymentConfig = useCallback(() => {
    return PayFastService.getConfig();
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset payment state
  const resetPayment = useCallback(() => {
    setIsProcessing(false);
    setError(null);
    setCurrentOrder(null);
  }, []);

  return {
    // State
    isProcessing,
    error,
    currentOrder,
    
    // Actions
    initiatePayment,
    handlePaymentSuccess,
    handlePaymentFailure,
    handlePaymentCancellation,
    getOrder,
    getCustomerOrders,
    processPayFastReturn,
    
    // Utilities
    isPayFastAvailable,
    getPaymentConfig,
    clearError,
    resetPayment
  };
};